import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { useSession } from '../auth/session';
import { Dialog } from '../components/Dialog';
import { ACTIVE_EVENT } from '../lib/activity';
import { useConfig } from '../lib/config';

// Automatisch abmelden (Kanzlei): nach idleMinutes ohne Eingabe (Maus, Tastatur, Tippen, Mausrad). Eingaben meldet
// die Seite höchstens einmal pro Minute dem Server (POST /api/auth/active). Er entscheidet, denn in einem anderen
// Tab kann noch jemand arbeiten. Zwei Minuten vorher fragt ein Hinweis „Sind Sie noch da?“. Ist die Zeit um, beendet
// der Server die Sitzung (401 „idle“), und die Anmeldeseite sagt, warum. Eine lange Arbeit, die jemand angestoßen hat
// (lib/activity.ts), zählt wie eine Eingabe, solange sie läuft.
const PING_MS = 60_000;
const WARN_MS = 2 * 60_000;
const CHECK_MS = 15_000;
// Kein „scroll“: Das kommt auch, wenn die Seite selbst scrollt (z. B. der Chat zu neuen Nachrichten), und hielte die
// Sitzung dann ewig offen. Scrollen von Hand meldet schon wheel, touchstart oder keydown.
const INPUT = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

interface Activity {
  idleMinutes: number | null;
  remainingMs: number | null;
}

export function IdleLogout() {
  const { expire } = useSession();
  const { idleMinutes } = useConfig();
  // Zeit bis zur Abmeldung, nur in den letzten zwei Minuten (sonst null = kein Hinweis)
  const [left, setLeft] = useState<number | null>(null);
  const stay = useRef<() => void>(() => {});

  useEffect(() => {
    if (!idleMinutes) return;
    let deadline = Date.now() + idleMinutes * 60_000;
    let lastPing = 0;
    let lastCheck = 0;
    let busy = false;
    let done = false;

    const apply = (a: Activity) => {
      if (a.remainingMs !== null) deadline = Date.now() + a.remainingMs;
    };
    const fail = (err: unknown) => {
      if (done || !(err instanceof ApiError) || err.status !== 401) return;
      done = true;
      setLeft(null);
      expire(err.code === 'idle' ? 'idle' : null);
    };
    const tick = () => {
      if (done) return;
      const rest = deadline - Date.now();
      // Kurz vor Schluss beim Server nachfragen (vielleicht wurde in einem anderen Tab gearbeitet); ist die Zeit um,
      // endet die Sitzung mit dieser Anfrage.
      if (rest <= WARN_MS && (rest <= 0 || Date.now() - lastCheck >= CHECK_MS)) call(false);
      setLeft(rest <= WARN_MS ? Math.max(0, rest) : null);
    };
    function call(touch: boolean) {
      if (busy || done) return;
      busy = true;
      if (touch) lastPing = Date.now();
      else lastCheck = Date.now();
      const req = touch ? api.post<Activity>('/api/auth/active') : api.get<Activity>('/api/auth/active');
      req.then(apply, fail).finally(() => {
        busy = false;
        tick();
      });
    }
    const onInput = () => {
      if (Date.now() - lastPing >= PING_MS) call(true);
    };
    stay.current = () => {
      lastPing = 0;
      call(true);
    };

    for (const t of INPUT) window.addEventListener(t, onInput, { capture: true, passive: true });
    window.addEventListener(ACTIVE_EVENT, onInput);
    document.addEventListener('visibilitychange', tick);
    const timer = setInterval(tick, 1000);
    call(false);
    return () => {
      done = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
      for (const t of INPUT) window.removeEventListener(t, onInput, { capture: true });
      window.removeEventListener(ACTIVE_EVENT, onInput);
    };
  }, [idleMinutes, expire]);

  if (left === null) return null;
  const s = Math.ceil(left / 1000);
  const time = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  return (
    <Dialog open onClose={() => stay.current()} title="Sind Sie noch da?" className="idle-dlg">
      <p>
        Aus Sicherheitsgründen werden Sie in <b>{time}</b> Minuten abgemeldet, weil eine Weile nichts eingegeben wurde.
      </p>
      <div className="dlg-actions">
        <button type="button" className="btn primary" onClick={() => stay.current()}>
          Angemeldet bleiben
        </button>
      </div>
    </Dialog>
  );
}
